import * as React from 'react'
import {ChangeEvent, Component, ReactElement} from 'react'
import {Box} from '@mui/material'
import {NatOwnershipTransfersRepo} from '../../repository/NatOwnershipTransfersRepo'
import NatCurrentDateTimeRepo from '../../../currentDateTime/repository/NatCurrentDateTimeRepo'
import HolidaysRepository from '../../../../../components/HolidaysRepository'
import NatCondominiumRepo from '../../../condominium/repository/NatCondominiumRepo'
import EventEmitterExGuest from '../../../../../components/shared/pageBaseComponents/EventEmitterExGuest'
import {AsyncCallFunctionType} from '../../../../../components/shared/hoc/withAsyncCommunication'
import moment from 'moment'
import {Messages} from '../../../../../Messages'
import {RequestItemError} from '../../../../../network/response/RequestItemError'
import {Nullable} from '../../../../../app/TypeHelpers'
import {NatGetCurrentDateTime} from '../../../currentDateTime/repository/NatGetCurrentDateTime'
import {HKConstants} from '../../../../../HKConstants'
import {FlexContainer, FullWidthColumnFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {BlockSpacer, currentMedia} from '../../../../../styledComponents/ResponsiveConvenience'
import styled from 'styled-components'
import ErrorMessageAreaWrapper from '../../../../../components/shared/forms/resident/ErrorMessageAreaWrapper'
import {DefaultStyledButton} from '../../../../../styledComponents/Button'
import {
    GuestApplyButton,
    GuestBackButton,
    ItemHeading,
    ListGridContainer,
    WideWhiteArea,
} from '../../common/NatOwnershipTransfersCommon'
import {NatZipCode} from '../../../condominium/repository/NatZipCode'
import {NatPostIntermediaryRequest} from '../../repository/NatPostIntermediaryRequest'
import NatGuestOwnershipTransfersDetailFormItem from '../../common/NatGuestOwnershipTransfersDetailFormItem'
import {convertToRoomNumberForResident} from '../../../../common/util/Natutil'
import {NatOwnershipTransferIntermediaryLocationState} from '../intermediary/NatGuestIntermediaryInterface'
import {Colors, FontSize, FontWeights} from '../../../../../styledComponents/Constants'
import {DisplayItemInputTitleNoMargin} from '../../../../../styledComponents/DisplayItem'
import {StyledDiv} from '../../../../../styledComponents/StyledTypeDefinitions'
import PPDatePickerForResident from '../../../../../components/shared/forms/resident/PPDatePickerForResident'
import {PPTextFieldForResident} from '../../../../../components/shared/forms/resident/PPTextFieldForResident'
import {
    GuestLoginInfoSessionStorageKey,
    NatGuestLoginInfoSessionStorageHelper,
} from '../../../../common/authentication/NatGuestLoginInfoSessionStorageHelper'
import {Location, NavigateFunction} from 'react-router-dom'


interface Props {
    condominiumCd: string
    applyId?: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    condominiumRepo: NatCondominiumRepo
    holidaysRepo: HolidaysRepository
    navigate: NavigateFunction
    location: Location<NatOwnershipTransferIntermediaryLocationState>
    eventEmitter: EventEmitterExGuest
    asyncCallFunction: AsyncCallFunctionType

    ownershipTransferDate: Nullable<string>
    companyName: Nullable<string>
    zipCode: Nullable<string>
    prefecture: Nullable<string>
    address2: Nullable<string>
    address3: Nullable<string>
    buildingRoomNumber: Nullable<string>
    representative: Nullable<string>
    phoneNumber: Nullable<string>
    mailAddress: Nullable<string>
    mailAddressConfirm: Nullable<string>

    submitButtonLabel: string
    onClickApplyConfirmButton: (request: NatPostIntermediaryRequest) => Promise<void>
    onClickBackButton: () => void
}

interface State {
    ownershipTransferDate: string
    companyName: string
    zipCode: string
    prefecture: string
    address2: string
    address3: string
    buildingRoomNumber: string
    representative: string
    phoneNumber: string
    mailAddress: string
    mailAddressConfirm: string

    currentDate: moment.Moment
    holidays: string[]

    applyButtonDisabled: boolean
    isLoadCompleted: boolean
}

export default class NatGuestIntermediaryForm extends Component<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            ownershipTransferDate: '',
            companyName: '',
            zipCode: '',
            prefecture: '',
            address2: '',
            address3: '',
            buildingRoomNumber: '',
            representative: '',
            phoneNumber: '',
            mailAddress: '',
            mailAddressConfirm: '',
            currentDate: moment(),
            holidays: [],
            applyButtonDisabled: true,
            isLoadCompleted: false,
        }

        this.handleOwnershipTransferDate = this.handleOwnershipTransferDate.bind(this)
        this.handleChangeText = this.handleChangeText.bind(this)
        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
        this.onClickBackButton = this.onClickBackButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            const [currentTimeResponse, holidays] = await Promise.all([
                this.getCurrentDateTime(),
                this.getHoliday(),
            ])

            if (currentTimeResponse && currentTimeResponse.details) {
                this.setState({
                    currentDate: moment(currentTimeResponse.details.currentDateTime),
                })
            }

            const ownershipTransferDate = this.props.ownershipTransferDate ?? this.state.ownershipTransferDate
            const companyName = this.props.companyName ?? this.state.companyName
            const zipCode = this.props.zipCode ?? this.state.zipCode
            const prefecture = this.props.prefecture ?? this.state.prefecture
            const address2 = this.props.address2 ?? this.state.address2
            const address3 = this.props.address3 ?? this.state.address3
            const buildingRoomNumber = this.props.buildingRoomNumber ?? this.state.buildingRoomNumber
            const representative = this.props.representative ?? this.state.representative
            const phoneNumber = this.props.phoneNumber ?? this.state.phoneNumber
            const mailAddress = this.props.mailAddress ?? this.state.mailAddress
            const mailAddressConfirm = this.props.mailAddressConfirm ?? this.state.mailAddressConfirm


            this.setState({
                ownershipTransferDate: ownershipTransferDate,
                companyName: companyName,
                zipCode: zipCode,
                prefecture: prefecture,
                address2: address2,
                address3: address3,
                buildingRoomNumber: buildingRoomNumber,
                representative: representative,
                phoneNumber: phoneNumber,
                mailAddress: mailAddress,
                mailAddressConfirm: mailAddressConfirm,
                holidays: holidays === null ? [] : holidays,
            }, () => {
                this.updateApplyConfirmButtonDisabled()
            })
        } finally {
            this.setState({isLoadCompleted: true})
            this.props.eventEmitter.closeLoading()
        }
    }

    async getCurrentDateTime(): Promise<Nullable<NatGetCurrentDateTime>> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        return await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    async getHoliday(): Promise<Nullable<string[]>> {
        const {holidaysRepo, eventEmitter} = this.props

        const minDate: string = HKConstants.CMN.SYC.MIN_HOLIDAY_DATE
        const maxDate: string = HKConstants.CMN.SYC.MAX_HOLIDAY_DATE
        return await this.props.asyncCallFunction(
            holidaysRepo.getHolidays.bind(holidaysRepo, minDate, maxDate),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    handleOwnershipTransferDate(date: string): void {
        this.setState({ownershipTransferDate: date}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeText(event: ChangeEvent<HTMLInputElement>): void {
        const key = event.target.name as keyof State
        const value: string = event.target.value
        this.setState({...this.state, [key]: value}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    updateApplyConfirmButtonDisabled(): void {
        const {
            ownershipTransferDate, companyName,
            prefecture, address2, address3,
            representative, phoneNumber,
        } = this.state

        let applyButtonDisabled = false

        if (!ownershipTransferDate || !companyName ||
            !prefecture || !address2 || !address3 ||
            !representative || !phoneNumber
        ) {
            applyButtonDisabled = true
        }
        this.setState({
            applyButtonDisabled: applyButtonDisabled,
        })
    }

    async onClickApplyConfirmButton(): Promise<void> {
        const {
            ownershipTransferDate, companyName, zipCode,
            prefecture, address2, address3,
            buildingRoomNumber, representative, phoneNumber,
            mailAddress, mailAddressConfirm,
        } = this.state
        const {applyId} = this.props

        const request: NatPostIntermediaryRequest = {
            applyId: applyId ?? null,
            ownershipTransferDate: ownershipTransferDate,
            companyName: companyName,
            zipCode: zipCode,
            prefecture: prefecture,
            address2: address2,
            address3: address3,
            buildingRoomNumber: buildingRoomNumber,
            representative: representative,
            phoneNumber: phoneNumber,
            mailAddress: mailAddress,
            mailAddressConfirm: mailAddressConfirm,
            version: this.props.location.state.applyInfo?.version ?? null,
            confirmed: false,
        }

        if (!request) {
            return
        }
        await this.props.onClickApplyConfirmButton(request)
    }

    onClickBackButton(): void {
        this.props.onClickBackButton()
    }

    async getAddressFromZipCode(): Promise<void> {
        const {zipCode} = this.state
        const {condominiumRepo} = this.props

        await this.props.eventEmitter.showLoading()

        try {
            // 桁数不正の場合、単純に結果が取得できないので特に事前チェックは行わない
            const firstPart = zipCode.substring(0, 3)
            const lastPart = zipCode.substring(3, zipCode.length)

            await condominiumRepo.getZipCode(firstPart, lastPart).then(
                zipData => {
                    const zipCodeResponse: NatZipCode = zipData
                    if (zipCodeResponse && zipCodeResponse.data
                        && zipCodeResponse.data[0] && zipCodeResponse.data[0].ja) {
                        this.setState({
                            prefecture: zipCodeResponse.data[0].ja.prefecture,
                            address2: zipCodeResponse.data[0].ja.address1,
                            address3: zipCodeResponse.data[0].ja.address2,
                        }, () => {
                            this.updateApplyConfirmButtonDisabled()
                        })
                    }
                    if (zipCodeResponse && zipCodeResponse.data
                        && zipCodeResponse.data.length > 1) {
                        const requestItemError = new RequestItemError(
                            'zipCode', Messages.WCONA0002.toString(), 'WCONA0002')
                        this.props.eventEmitter.emit(requestItemError.resultCode, requestItemError)

                    }
                },
            ).catch(
                () => {
                    const requestItemError = new RequestItemError(
                        'zipCode', Messages.ECONA0001.toString(), 'ECONA0001')
                    this.props.eventEmitter.emit(requestItemError.resultCode, requestItemError)
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    renderTargetRoomInfo(): ReactElement {
        const applicantRoom = NatGuestLoginInfoSessionStorageHelper
                .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME) + '　' +
            NatGuestLoginInfoSessionStorageHelper
                .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_RIDGE_NAME) + ' ' +
            convertToRoomNumberForResident(
                NatGuestLoginInfoSessionStorageHelper
                    .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER),
            )

        return (
            <>
                <ItemHeading>
                    対象住戸
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    {applicantRoom}
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderOwnershipTransfersDateInfo(): ReactElement {
        // 組合員資格の変更年月日
        const {ownershipTransferDate, holidays} = this.state
        return (
            <>
                <ItemHeading>
                    組合員変更年月日
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={<span>組合員資格の変更日</span>}
                        required
                        component={
                            <PPDatePickerForResident
                                date={ownershipTransferDate}
                                onChange={this.handleOwnershipTransferDate}
                                holidays={holidays}
                                itemValue={''}
                                eventKeys={['ownershipTransferDate']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderPaymentChangeDeadLineInfo(): ReactElement {
        // 管理費等の支払変更期日(旧組合員)、管理費等の支払変更期日(新組合員)
        const ownershipTransferDate =
            this.state.ownershipTransferDate ? moment(this.state.ownershipTransferDate) : null

        const oldMemberDate =
            ownershipTransferDate?.clone().add(1, 'months') ?? null // 翌月

        const newMemberDate =
            ownershipTransferDate?.clone().add(2, 'months') ?? null // 翌々月
        return (
            <>
                <ItemHeading>
                    管理費等の支払変更期日
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    組合員資格変更年月日の翌月分までを旧組合員の支払い、翌々月からを新組合員の支払いとします。
                    <Box height={'20px'}/>
                    <ChangeDate>
                        旧組合員
                        <ChangeDateYearInfo>
                            <ChangeDateYear>{oldMemberDate ? oldMemberDate.year() : ''}</ChangeDateYear>年
                        </ChangeDateYearInfo>
                        <ChangeDateMonthInfo>
                            <ChangeDateMonth>{oldMemberDate ? oldMemberDate.month() + 1 : ''}</ChangeDateMonth>月分まで支払い
                        </ChangeDateMonthInfo>
                    </ChangeDate>
                    <ChangeDate>
                        新組合員
                        <ChangeDateYearInfo>
                            <ChangeDateYear>{newMemberDate ? newMemberDate.year() : ''}</ChangeDateYear>年
                        </ChangeDateYearInfo>
                        <ChangeDateMonthInfo>
                            <ChangeDateMonth>{newMemberDate ? newMemberDate.month() + 1 : ''}</ChangeDateMonth>月分から支払い
                        </ChangeDateMonthInfo>
                    </ChangeDate>
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderIntermediaryInfoForm(): ReactElement {
        const {
            companyName,
            zipCode,
            prefecture,
            address2,
            address3,
            buildingRoomNumber,
            representative,
            phoneNumber,
            mailAddress,
            mailAddressConfirm,
        } = this.state

        return (
            <>
                <ItemHeading>
                    仲介業者情報
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'企業名'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'companyName',
                                    value: companyName,
                                    placeholder: '例：長谷工不動産',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['companyName']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'郵便番号（ハイフン不要）'}
                        component={
                            <ErrorMessageAreaWrapper>
                                <FlexContainer>
                                    <Box flex={2} minWidth={0}>
                                        <PPTextFieldForResident
                                            inputProps={{
                                                maxLength: 7,
                                                name: 'zipCode',
                                                value: zipCode,
                                                placeholder: '例：1234567',
                                            }}
                                            onChange={this.handleChangeText}
                                            eventKeys={['zipCode']}
                                            eventEmitter={this.props.eventEmitter}
                                        />
                                    </Box>
                                    <FlexContainer>
                                        <BlockSpacer width={5}/>
                                    </FlexContainer>
                                    <Box flex={1} maxWidth={'160px'} minWidth={'125px'} maxHeight={'50px'}>
                                        <DefaultStyledButton
                                            onClick={() => this.getAddressFromZipCode()}>
                                            住所自動入力
                                        </DefaultStyledButton>
                                    </Box>
                                </FlexContainer>
                            </ErrorMessageAreaWrapper>
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'都道府県'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 4,
                                    name: 'prefecture',
                                    value: prefecture,
                                    placeholder: '例：東京都',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['prefecture']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'市区町村'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'address2',
                                    value: address2,
                                    placeholder: '例：港区',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['address2']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'番地'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'address3',
                                    value: address3,
                                    placeholder: '例：芝４－６－６',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['address3']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'建物名・部屋番号'}
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'buildingRoomNumber',
                                    value: buildingRoomNumber,
                                    placeholder: '例：長谷工芝二ビル　5F',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['buildingRoomNumber']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'担当者'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 20,
                                    name: 'representative',
                                    value: representative,
                                    placeholder: '例：素敵 太郎',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['representative']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={<span>電話番号<BreakPcOnly/>（ハイフン不要）</span>}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 11,
                                    name: 'phoneNumber',
                                    value: phoneNumber,
                                    placeholder: '例：0312345678',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['phoneNumber']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <Box color={Colors.Body}>メールアドレスをご入力いただくと、提出完了メールを受け取ることができます。</Box>
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'メールアドレス'}
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 100,
                                    name: 'mailAddress',
                                    value: mailAddress,
                                    placeholder: '例：mail@example.com',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['mailAddress']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'メールアドレス（確認用）'}
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 100,
                                    name: 'mailAddressConfirm',
                                    value: mailAddressConfirm,
                                    placeholder: '例：mail@example.com',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['mailAddressConfirm']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderCaution(): ReactElement {
        return (
            <WideWhiteArea>
                <Box height={'20px'}/>
                <Box fontWeight={FontWeights.Bold}>【注意事項】</Box>
                <Box height={'10px'}/>
                <ListGridContainer fontSize={FontSize.Middle}>
                    <Box>・</Box>
                    <DisplayItemInputTitleNoMargin>
                        <Box component={'span'} color={Colors.Body}>
                            メールが届かない場合は、ご契約のプロバイダやメールソフトの設定などにより、受信ができない可能性がございます。
                        </Box>
                    </DisplayItemInputTitleNoMargin>
                    <Box>・</Box>
                    <DisplayItemInputTitleNoMargin>
                        <Box component={'span'} color={Colors.Body}>
                            迷惑メールの対策で、ドメイン受信設定を行っている場合は、「@suteki.haseko.jp」「@haseko.co.jp」を受信設定してください。
                            設定方法はご契約のプロバイダのWebサイトをご確認ください。
                        </Box>
                    </DisplayItemInputTitleNoMargin>
                    <Box>・</Box>
                    <DisplayItemInputTitleNoMargin>
                        <Box component={'span'} color={Colors.Body}>
                            yahooメール/gmail等のフリーメールアドレスでもご登録いただけます。スマートフォンをご利用のお客様でも、
                            SMSにて受信するメールアドレスをご利用の方は、確認メールが正しく表示されず、ご登録いただけませんので、ご注意ください。
                        </Box>
                    </DisplayItemInputTitleNoMargin>
                </ListGridContainer>
                <Box height={'20px'}/>
            </WideWhiteArea>
        )
    }

    render(): ReactElement {
        const {submitButtonLabel} = this.props
        const {applyButtonDisabled} = this.state

        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                {this.renderTargetRoomInfo()}
                <Box height={'15px'}/>
                {this.renderOwnershipTransfersDateInfo()}
                <Box height={'15px'}/>
                {this.renderPaymentChangeDeadLineInfo()}
                <Box height={'15px'}/>
                {this.renderIntermediaryInfoForm()}
                {this.renderCaution()}
                <Box height={'20px'}/>
                <GuestApplyButton disabled={applyButtonDisabled} onClick={this.onClickApplyConfirmButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            {submitButtonLabel}
                        </div>
                    </FullWidthColumnFlexContainer>
                </GuestApplyButton>
                <Box height={'30px'}/>
                <GuestBackButton onClick={this.onClickBackButton}>
                    戻る
                </GuestBackButton>
                <Box height={'15px'}/>
            </>
        )
    }
}

const BreakPcOnly = styled('br')`
  ${currentMedia.lessThan('sp')`
    display: none;
  `};
`
const ChangeDate: StyledDiv = styled.div`
  display: flex;
`

const ChangeDateYearInfo: StyledDiv = styled.div`
  border: none;
  justify-content: flex-start;
  text-align: right;
  width: 85px;
  display: flex;
`

const ChangeDateMonthInfo = styled(ChangeDateYearInfo)`
  width: 150px;
`

const ChangeDateYear: StyledDiv = styled.div`
  width: 80px;
  font-weight: ${FontWeights.Bold};
`

const ChangeDateMonth = styled(ChangeDateYear)`
  width: 35px;
`
